import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  MainTableFilterModel,
  PrintReportFilterModel,
} from 'src/app/shared/models/main-table-filter.model';
import { getDepartmentId, getTenantId } from 'src/app/services/store-snapshot.service';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { environment } from 'src/environments/environment';
import { FormDataService } from 'src/app/shared/attachments/service/form-data.service';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import {
  IApiResponseGroupMedicines,
  ICreateMedicineRequest,
  ICreateMedicineResponse,
  IMedicineResponse,
  IUpdateMedicineRequest,
} from '../models/medicine.model';
import { MedicineEvent } from '../enums/medicine-event.enum';
import {
  IAddMedicineStockRequest,
  IAdjustMedicineStockRequest,
  IUseMedicineRequest,
} from '../models/use-medicine.model';
import { IMedicineStock, IPagedMedicineStock } from '../models/medicine-stock.model';

const COLUMN_TO_KEY_MAP: any = {
  refrigerated: 'ShouldBeRefrigerated',
  quantityType: 'QuantityType.Name',
  medicineCategory: 'Category.Name',
  minQuantity: 'MinimumQuantity',
  responsiblePositions: 'ResponsibleForPositions.Name',
  responsiblePositionGroups: 'ResponsibleForPositionGroups.Name',
  responsibleRoles: 'ResponsibleForRoles.Name',
};
const OR_GROUP_MAP: Record<string, string> = {
  responsiblePositions: 'responsible',
  responsiblePositionGroups: 'responsible',
  responsibleRoles: 'responsible',
};
const STOCK_COLUMN_TO_KEY_MAP: Record<string, string> = {
  quantity: 'QuantityChange',
  expires: 'Expires',
  description: 'Reason',
  date: 'Created',
  changedBy: 'ChangedBy',
  event: 'MedicineEvent',
};
@Injectable({
  providedIn: 'root',
})
export class MedicineApiService {
  headers = { 'content-type': 'application/json' };

  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/medicine`;
  }

  constructor(private http: HttpClient) {}

  getPagedMedicines(
    filter?: MainTableFilterModel,
    menuId?: string
  ): Observable<IApiResponseGroupMedicines> {
    let params = createFilterParams(filter);
    if (menuId) {
      params = params.set('MenuId', menuId);
    }
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      filter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );

    return this.http.get<IApiResponseGroupMedicines>(this.url, { params });
  }

  getAllMedicines(): Observable<IMedicineResponse[]> {
    const url = `${this.url}/List`;
    return this.http.get<IMedicineResponse[]>(url);
  }

  getMedicineById(medicineId: string): Observable<IMedicineResponse> {
    const url = `${this.url}/${medicineId}`;
    return this.http.get<IMedicineResponse>(url);
  }

  createMedicine(medicine: ICreateMedicineRequest): Observable<ICreateMedicineResponse> {
    return this.http.post<ICreateMedicineResponse>(this.url, medicine, { headers: this.headers });
  }

  updateMedicine(medicine: IUpdateMedicineRequest): Observable<void> {
    return this.http.put<void>(this.url, medicine, { headers: this.headers });
  }

  deleteMedicine(id: string): Observable<void> {
    const url = `${this.url}/${id}`;
    return this.http.delete<void>(url);
  }

  useMedicine(medicine: IUseMedicineRequest): Observable<void> {
    const formData = FormDataService.mapToFormData(medicine);
    const url = `${this.url}/${medicine.id}/${MedicineEvent.Use}`;
    return this.http.put<void>(url, formData);
  }

  addtoStock(medicine: IAddMedicineStockRequest): Observable<void> {
    const formData = FormDataService.mapToFormData(medicine);
    const url = `${this.url}/${medicine.id}/${MedicineEvent.Add}`;
    return this.http.put<void>(url, formData);
  }

  adjustStock(medicine: IAdjustMedicineStockRequest): Observable<void> {
    const formData = FormDataService.mapToFormData(medicine);
    const url = `${this.url}/${medicine.id}/${MedicineEvent.Adjust}`;
    return this.http.put<void>(url, formData);
  }

  getMedicineStock(
    medicineId: string,
    currentFilter?: MainTableFilterModel,
    stockVersionId?: string
  ): Observable<IPagedMedicineStock> {
    const url = `${this.url}/${medicineId}/Stock`;
    let params = createFilterParams(currentFilter, undefined, STOCK_COLUMN_TO_KEY_MAP);
    if (stockVersionId) {
      params = params.set('StockVersionId', stockVersionId);
    }
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      currentFilter,
      STOCK_COLUMN_TO_KEY_MAP
    );

    return this.http.get<IPagedMedicineStock>(url, { params });
  }

  printReport(filter?: PrintReportFilterModel, menuId?: string): Observable<Blob> {
    const url = `${this.url}/Report`;
    let params = createFilterParams(filter);

    if (menuId) {
      params = params.set('MenuId', menuId);
    }

    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      filter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );
    return this.http.get(url, { params, responseType: 'blob' });
  }
}
